import { createApi } from '@reduxjs/toolkit/query/react';
import type {
  DocumentDto,
  DocumentLabelDto,
  DocumentContentDto,
  ListDocumentsResult,
  UploadDocumentResult,
  BulkUploadResult,
  DocumentListParams,
  ImportJobDto,
  GoogleDriveImportRequest,
  OneDriveImportRequest,
  ImportProviderConfig,
  ApproveMetadataRequest,
} from '@/types';
import { baseQueryWithRetry } from './baseQuery';
import { TAG_DOCUMENT, TAG_IMPORT_JOB, TAG_LABEL } from '@/store/tagTypes';

/**
 * How often to refetch document data while a document is still processing. Status transitions
 * (parse → label → embed → ready) happen out of band on Service Bus workers, so the UI polls
 * to observe them. Callers must gate polling on "is anything still processing" and disable it
 * otherwise, so there is no idle load. 4s keeps the UI responsive without hammering the DB.
 */
export const DOCUMENT_POLL_INTERVAL_MS = 4000;

export const documentsApi = createApi({
  reducerPath: 'documentsApi',
  baseQuery: baseQueryWithRetry,
  tagTypes: [TAG_DOCUMENT, TAG_IMPORT_JOB, TAG_LABEL],
  endpoints: (builder) => ({
    // --- Documents ---

    listDocuments: builder.query<ListDocumentsResult, DocumentListParams | void>({
      query: (params) => ({
        url: '/documents',
        params: params
          ? {
              page: params.page?.toString(),
              pageSize: params.pageSize?.toString(),
              status: params.status,
              type: params.type,
              labelIds: params.labelIds,
              sortBy: params.sortBy,
              sortOrder: params.sortOrder,
            }
          : undefined,
      }),
      providesTags: (result) =>
        result?.items
          ? [
              ...result.items.map(({ id }) => ({ type: TAG_DOCUMENT, id })),
              { type: TAG_DOCUMENT, id: 'LIST' },
            ]
          : [{ type: TAG_DOCUMENT, id: 'LIST' }],
    }),

    getDocument: builder.query<DocumentDto, string>({
      query: (id) => `/documents/${id}`,
      providesTags: (_result, _error, id) => [{ type: TAG_DOCUMENT, id }],
    }),

    getDocumentContent: builder.query<DocumentContentDto, string>({
      query: (id) => `/documents/${id}/content`,
    }),

    uploadDocument: builder.mutation<
      UploadDocumentResult,
      { file: File; fileName: string; documentName?: string; customerId?: string; sprawaId?: string }
    >({
      query: ({ file, fileName, documentName, customerId, sprawaId }) => {
        const params = new URLSearchParams({ fileName });
        if (documentName) params.set('documentName', documentName);
        if (customerId) params.set('customerId', customerId);
        if (sprawaId) params.set('sprawaId', sprawaId);
        return {
          url: `/documents?${params.toString()}`,
          method: 'POST',
          body: file,
        };
      },
      invalidatesTags: [{ type: TAG_DOCUMENT, id: 'LIST' }],
    }),

    bulkUploadDocuments: builder.mutation<
      BulkUploadResult,
      { files: { fileName: string; base64Content: string }[] }
    >({
      query: (body) => ({
        url: '/documents/bulk',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: TAG_DOCUMENT, id: 'LIST' }],
    }),

    deleteDocument: builder.mutation<void, string>({
      query: (id) => ({
        url: `/documents/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: TAG_DOCUMENT, id },
        { type: TAG_DOCUMENT, id: 'LIST' },
      ],
    }),

    retryDocument: builder.mutation<void, string>({
      query: (id) => ({
        url: `/documents/${id}/retry`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: TAG_DOCUMENT, id },
        { type: TAG_DOCUMENT, id: 'LIST' },
      ],
    }),

    approveDocumentMetadata: builder.mutation<void, { id: string; body: ApproveMetadataRequest }>({
      query: ({ id, body }) => ({
        url: `/documents/${id}/approve-metadata`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: TAG_DOCUMENT, id },
        { type: TAG_DOCUMENT, id: 'LIST' },
      ],
    }),

    updateDocumentName: builder.mutation<DocumentDto, { id: string; documentName: string | null }>({
      query: ({ id, documentName }) => ({
        url: `/documents/${id}/name`,
        method: 'PATCH',
        body: { documentName },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: TAG_DOCUMENT, id },
        { type: TAG_DOCUMENT, id: 'LIST' },
      ],
    }),

    rejectDocumentMetadata: builder.mutation<void, string>({
      query: (id) => ({
        url: `/documents/${id}/reject-metadata`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, id) => [{ type: TAG_DOCUMENT, id }],
    }),

    reassignDocumentSprawa: builder.mutation<void, { id: string; sprawaId: string | null }>({
      query: ({ id, sprawaId }) => ({
        url: `/documents/${id}/sprawa`,
        method: 'PATCH',
        body: { sprawaId },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: TAG_DOCUMENT, id },
        { type: TAG_DOCUMENT, id: 'LIST' },
      ],
    }),

    uploadDocumentVersion: builder.mutation<UploadDocumentResult, { parentId: string; file: File; fileName: string }>({
      query: ({ parentId, file, fileName }) => ({
        url: `/documents/${parentId}/versions?fileName=${encodeURIComponent(fileName)}`,
        method: 'POST',
        body: file,
      }),
      invalidatesTags: (_result, _error, { parentId }) => [
        { type: TAG_DOCUMENT, id: parentId },
        { type: TAG_DOCUMENT, id: 'LIST' },
      ],
    }),

    // --- Labels ---

    listLabels: builder.query<DocumentLabelDto[], void>({
      query: () => '/labels',
      providesTags: [{ type: TAG_LABEL, id: 'LIST' }],
    }),

    // --- Import Provider Config ---

    getImportProviderConfig: builder.query<ImportProviderConfig, void>({
      query: () => '/config/import-providers',
    }),

    // --- Import Jobs ---

    importGoogleDrive: builder.mutation<ImportJobDto, GoogleDriveImportRequest>({
      query: (body) => ({
        url: '/import/google-drive',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: TAG_IMPORT_JOB, id: 'LIST' }],
    }),

    importOneDrive: builder.mutation<ImportJobDto, OneDriveImportRequest>({
      query: (body) => ({
        url: '/import/onedrive',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: TAG_IMPORT_JOB, id: 'LIST' }],
    }),

    getImportJob: builder.query<ImportJobDto, string>({
      query: (id) => `/import-jobs/${id}`,
      providesTags: (_result, _error, id) => [{ type: TAG_IMPORT_JOB, id }],
    }),

    listImportJobs: builder.query<ImportJobDto[], void>({
      query: () => '/import-jobs',
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: TAG_IMPORT_JOB, id })),
              { type: TAG_IMPORT_JOB, id: 'LIST' },
            ]
          : [{ type: TAG_IMPORT_JOB, id: 'LIST' }],
    }),
  }),
});

export const {
  useListDocumentsQuery,
  useGetDocumentQuery,
  useGetDocumentContentQuery,
  useLazyGetDocumentContentQuery,
  useUploadDocumentMutation,
  useUpdateDocumentNameMutation,
  useBulkUploadDocumentsMutation,
  useDeleteDocumentMutation,
  useRetryDocumentMutation,
  useApproveDocumentMetadataMutation,
  useRejectDocumentMetadataMutation,
  useReassignDocumentSprawaMutation,
  useUploadDocumentVersionMutation,
  useListLabelsQuery,
  useGetImportProviderConfigQuery,
  useImportGoogleDriveMutation,
  useImportOneDriveMutation,
  useGetImportJobQuery,
  useListImportJobsQuery,
} = documentsApi;
